import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface CompanyTest {
  id: string;
  title: string;
  description: string;
  time_limit: number | null;
  total_time_limit: number | null;
  time_limit_type: string;
  share_token: string;
  invitation_only: boolean;
  isInvited: boolean;
  hasAlreadyTaken: boolean;
  questionCount: number;
  total_question_time_seconds: number;
  testType: 'public' | 'invited';
  job_title: string | null;
}

export function useCompanyTests(organizationId: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['company-tests', organizationId, user?.email],
    queryFn: async () => {
      console.log('[CompanyTests] Starting query:', { organizationId, userEmail: user?.email });
      
      if (!user?.email) {
        console.log('[CompanyTests] No user email, returning empty arrays');
        return { publicTests: [], invitedTests: [] };
      }

      // Fetch public tests
      const { data: publicTestsData, error: publicError } = await supabase
        .from('tests_with_question_counts')
        .select(`
          id,
          title,
          description,
          time_limit,
          total_time_limit,
          time_limit_type,
          share_token,
          invitation_only,
          job_title,
          question_count,
          total_question_time_seconds
        `)
        .eq('organization_id', organizationId)
        .eq('invitation_only', false)
        .eq('status', 'published')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (publicError) throw publicError;

      // Fetch invited tests
      const { data: invitedTestsData, error: invitedError } = await supabase
        .from('test_invitations')
        .select(`
          test_id,
          tests:tests_with_question_counts!inner (
            id,
            title,
            description,
            time_limit,
            total_time_limit,
            time_limit_type,
            share_token,
            invitation_only,
            job_title,
            question_count,
            total_question_time_seconds
          )
        `)
        .eq('email', user.email)
        .eq('tests.organization_id', organizationId)
        .in('status', ['pending', 'accepted', 'sent'])
        .order('invited_at', { ascending: false });

      if (invitedError) throw invitedError;

      // Get user's completed test responses
      const { data: responsesData } = await supabase
        .from('test_responses')
        .select('test_id')
        .eq('candidate_id', user.id)
        .in('status', ['completed', 'abandoned']);

      const completedTestIds = new Set(responsesData?.map(r => r.test_id) || []);
      
      console.log('[CompanyTests] Completed test IDs:', Array.from(completedTestIds));

      // Get user's in-progress test responses for this organization
      const { data: inProgressData } = await supabase
        .from('test_responses')
        .select(`
          id,
          test_id,
          started_at,
          last_question_index,
          tests!inner (
            id,
            title,
            organization_id,
            questions:questions (count)
          )
        `)
        .eq('candidate_id', user.id)
        .eq('status', 'in_progress')
        .eq('tests.organization_id', organizationId)
        .order('started_at', { ascending: false });

      console.log('[CompanyTests] In-progress tests:', inProgressData);

      const inProgressTests = inProgressData || [];

      // Transform public tests
      const publicTests: CompanyTest[] = (publicTestsData || []).map(test => ({
        id: test.id,
        title: test.title,
        description: test.description || '',
        time_limit: test.time_limit,
        total_time_limit: test.total_time_limit,
        time_limit_type: test.time_limit_type,
        share_token: test.share_token,
        invitation_only: test.invitation_only,
        isInvited: false,
        hasAlreadyTaken: completedTestIds.has(test.id),
        questionCount: test.question_count || 0,
        total_question_time_seconds: test.total_question_time_seconds || 0,
        testType: 'public',
        job_title: test.job_title,
      }));

      // Transform invited tests
      const invitedTests: CompanyTest[] = (invitedTestsData || [])
        .filter(invitation => (invitation as any).tests !== null)
        .map(invitation => {
          const test = (invitation as any).tests;
          
          return {
            id: test.id,
            title: test.title,
            description: test.description || '',
            time_limit: test.time_limit,
            total_time_limit: test.total_time_limit,
            time_limit_type: test.time_limit_type,
            share_token: test.share_token,
            invitation_only: test.invitation_only,
            isInvited: true,
            hasAlreadyTaken: completedTestIds.has(test.id),
            questionCount: test.question_count || 0,
            total_question_time_seconds: test.total_question_time_seconds || 0,
            testType: 'invited' as const,
            job_title: test.job_title,
          };
        });

      // Remove public tests that user is invited to (invited tests take priority)
      const invitedTestIds = new Set(invitedTests.map(t => t.id));
      const filteredPublicTests = publicTests.filter(test => !invitedTestIds.has(test.id));

      console.log('[CompanyTests] Deduplication:', {
        originalPublicCount: publicTests.length,
        invitedTestIds: Array.from(invitedTestIds),
        filteredPublicCount: filteredPublicTests.length,
        removedDuplicates: publicTests.filter(t => invitedTestIds.has(t.id)).map(t => t.title)
      });

      console.log('[CompanyTests] Final results:', {
        publicTests: filteredPublicTests.map(t => ({ id: t.id, title: t.title })),
        invitedTests: invitedTests.map(t => ({ id: t.id, title: t.title })),
        inProgressTests: inProgressTests.map(t => ({ id: t.id, testId: t.test_id })),
        publicCount: filteredPublicTests.length,
        invitedCount: invitedTests.length,
        inProgressCount: inProgressTests.length
      });

      return { publicTests: filteredPublicTests, invitedTests, inProgressTests };
    },
    enabled: !!user?.email,
  });
}
